/**
 * Analysis Results Component
 * 
 * Component that displays structured GenAI maintenance analysis results
 * with proper Meridian components and minimalistic design.
 */

import React from 'react';
import { css } from '@emotion/css';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Text from '@amzn/meridian/text';
import { MaintenanceAnalysisResponse } from '../../../services/equipment/types';
import { Badge, Row } from '@amzn/meridian';

interface AnalysisResultsProps {
  analysis: MaintenanceAnalysisResponse;
  
}
interface AnalysisSectionProps {
  title: string;
  children: React.ReactNode;
}

interface CountWithWorkOrdersProps {
  count: number;
  workOrderIds: string[];
  label?: string;
}

/**
 * Displays count with work order IDs when count > 0
 */
const CountWithWorkOrders: React.FC<CountWithWorkOrdersProps> = ({
  count,
  workOrderIds,
  label = "Count"
}) => {
  if (count === 0) {
    return <Text type="b100">{label}: {count}</Text>;
  }

  return (
    <Column spacing="100">
      <Text type="b100">{label}: {count}</Text>
      {workOrderIds.length > 0 && (
        <Text type="b100" color="secondary">
          Work Orders: {workOrderIds.join(', ')}
        </Text>
      )}
    </Column>
  );
};

const CustomDot = () => (
  <div
    style={{
      width: '5px',
      height: '5px',
      borderRadius: '50%',
      backgroundColor: '#000000ff',
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

/**
 * Clean section for analysis results using Meridian components
 */
const AnalysisSection: React.FC<AnalysisSectionProps> = ({
  title,
  children
}) => {
  const sectionStyles = css({
    marginBottom: '16px'
  });
  
  const contentStyles = css({
    paddingLeft: '16px'
  });

  return (
    <Column spacing="200" className={sectionStyles}>
      <Text type="h200">{title}</Text>
      <div className={contentStyles}>
        {children}
      </div>
    </Column>
  );
};

/**
 * Analysis Results display component
 */
export const AnalysisResults: React.FC<AnalysisResultsProps> = ({ analysis }) => {
  if (!analysis.data) {
    return (
      <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        <Text type="b200">No analysis data available</Text>
      </div>
    );
  }

  const { data } = analysis;

  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      <Column spacing="300">
        {/* Summary Header */}
        <Text type="b200" color="secondary">
          Analyzed {analysis.total_records} work orders from {analysis.date_range}
        </Text>

        <Divider />

        {/* Recurring Problems */}
        {data.recurring_problems && data.recurring_problems.length > 0 && (
          <AnalysisSection title="Recurring Problems">
            <Column spacing="300">
              {data.recurring_problems.map((problem, index) => (
                <Column key={index} spacing="100">
                  <Text type="h50">{problem.problem_type}</Text>
                  <CountWithWorkOrders 
                    count={problem.count}
                    workOrderIds={problem.work_order_ids}
                  />
                  <Row spacing="300">
                    <Text type="b200">{problem.description}</Text>
                  </Row>
                </Column>
              ))}
            </Column>
          </AnalysisSection>
        )}
        <Divider/>

        {/* Spare Parts Replacement */}
        {data.spare_parts_replacement && (
          <AnalysisSection title="Spare Parts Replacement">
            <Column spacing="100">
              <CountWithWorkOrders 
                count={data.spare_parts_replacement.count}
                workOrderIds={data.spare_parts_replacement.work_order_ids}
              />
              <Row spacing="300">
              <Text type="b200">{data.spare_parts_replacement.description}</Text>
              </Row>
            </Column>
          </AnalysisSection>
        )}
        <Divider/>

        {/* Maintenance Patterns */}
        {data.other_patterns?.preventive_maintenance && (
          <AnalysisSection title="Maintenance Patterns">
            <Column spacing="200">
              <Text type="h50">Preventive Maintenance</Text>
              <Column spacing="100">
                <CountWithWorkOrders 
                  count={data.other_patterns.preventive_maintenance.count}
                  workOrderIds={data.other_patterns.preventive_maintenance.work_order_ids}
                />
                <Row spacing="300">
                  <Text type="b200">{data.other_patterns.preventive_maintenance.description}</Text>
                </Row>
              </Column>
            </Column>
          </AnalysisSection>
        )}

        <Divider/>

        {/* Work Order Relationships */}
        {data.work_order_relationships && (
          <AnalysisSection title="Work Order Relationships">
            <Column spacing="300">
              {data.work_order_relationships.maintenance_sequences && (
                <Column spacing="100">
                  <Text type="h50">Maintenance Sequences</Text>
                  <CountWithWorkOrders 
                    count={data.work_order_relationships.maintenance_sequences.count}
                    workOrderIds={data.work_order_relationships.maintenance_sequences.work_order_ids}
                  />
                  <Row spacing="300">
                    <Text type="b200">{data.work_order_relationships.maintenance_sequences.description}</Text>
                  </Row>
                </Column>
              )}
              
              {data.work_order_relationships.time_gaps && (
                <Column spacing="100">
                  <Text type="h50">Time Gaps</Text>
                  <CountWithWorkOrders 
                    count={data.work_order_relationships.time_gaps.count}
                    workOrderIds={data.work_order_relationships.time_gaps.work_order_ids}
                  />
                  <Row spacing="300">
                    <Text type="b200">{data.work_order_relationships.time_gaps.description}</Text>
                  </Row>
                </Column>
              )}
            </Column>
          </AnalysisSection>
        )}
      </Column>
    </div>
  );
};
